/* eslint-disable id-length -- We are mapping letters with colors */

import { Avatar } from '@mui/material';
import {
  blue,
  blueGrey,
  brown,
  cyan,
  green,
  grey,
  indigo,
  lightGreen,
  orange,
  pink,
  purple,
  red,
  teal,
  yellow,
} from '@mui/material/colors';
import { makeStyles } from '@mui/styles';
import classnames from 'classnames';
import { get } from 'lodash';
import { FieldProps, useRecordContext } from 'react-admin';

import { formatFullname } from '@sh4res/bo-common/utils';

type AvatarFieldProps = {
  variant: 'small' | 'large' | 'huge';
} & FieldProps;

export const AvatarField = (props: AvatarFieldProps) => {
  const { variant, source = 'profile_pic' } = props;

  const record = useRecordContext(props);

  const fullname = formatFullname(record);
  const image = get(record, source);

  const classes = useStyles({ color: colorFromLetter(fullname) });

  return (
    <Avatar
      variant="rounded"
      className={classnames(classes.root, classes[variant])}
      src={image}
      alt={fullname}
    />
  );
};

AvatarField.defaultProps = {
  variant: 'small',
};

type AvatarColorType = {
  [key: string]: string;
};

const colors: AvatarColorType = {
  // Light colors
  a: red[300],
  b: blueGrey[300],
  c: purple[300],
  d: indigo[300],
  e: blue[300],
  f: cyan[300],
  g: teal[300],
  h: green[300],
  i: lightGreen[300],
  j: yellow[300],
  k: orange[300],
  l: brown[300],
  m: pink[300],
  // Dark colors
  n: red[700],
  o: blueGrey[700],
  p: purple[700],
  q: indigo[700],
  r: blue[700],
  s: cyan[700],
  t: teal[700],
  u: green[700],
  v: lightGreen[700],
  w: yellow[700],
  x: orange[700],
  y: brown[700],
  z: pink[700],
};

const { 300: defaultColor } = grey;

export function colorFromLetter(letter = '', colorsMap = colors): string {
  if (!letter) {
    return defaultColor;
  }
  const firstLetter = letter[0].toLowerCase();
  return colorsMap[firstLetter] ? colorsMap[firstLetter] : defaultColor;
}

type AvatarProps = {
  color: string;
};

const useStyles = makeStyles((theme) => ({
  root: {
    textTransform: 'capitalize',
    backgroundColor: (props: AvatarProps) => props.color || defaultColor,
    color: (props: AvatarProps) => theme.palette.getContrastText(props.color || defaultColor),
  },
  small: {
    width: theme.spacing(3),
    height: theme.spacing(3),
    '& > span': {
      fontSize: theme.typography.pxToRem(12),
    },
  },
  large: {
    width: theme.spacing(7),
    height: theme.spacing(7),
    '& > span': {
      fontSize: theme.typography.pxToRem(18),
    },
  },
  huge: {
    width: theme.spacing(16),
    height: theme.spacing(16),
    '& > span': {
      fontSize: theme.typography.pxToRem(30),
    },
  },
}));
